import { useMemo } from 'react';

import { ApiClient, formatMoney, useAsync } from '@clientma/core';
import { Alert, Badge, Button, Card, Skeleton } from '@clientma/ui';

import { providerApi, type CurrencyTotal } from '../api';

/**
 * What the business looks like this morning.
 *
 * Ordered by what somebody would act on rather than by what is easiest to count: money waiting
 * on a decision first, then trials about to lapse, then the shape of the customer base. A
 * dashboard that opens with a total tenant count tells you nothing you can do anything about.
 */
export default function Overview({
  api,
  onOpenPayments,
}: {
  api: ApiClient;
  onOpenPayments: () => void;
}) {
  const provider = useMemo(() => providerApi(api), [api]);

  const { data, isLoading, error } = useAsync(
    (signal) => provider.overview(signal),
    [provider],
  );

  if (isLoading) return <Skeleton height={320} />;

  if (error || !data) {
    return (
      <Alert tone="danger" title="The overview could not be loaded">
        {error?.message ?? 'Try again in a moment.'}
      </Alert>
    );
  }

  return (
    <div className="cma-stack">
      {data.paymentsAwaitingReview > 0 && (
        <Alert tone="warning" title="Payments are waiting on you">
          <div className="provider-actionrow">
            <span>
              {data.paymentsAwaitingReview}{' '}
              {data.paymentsAwaitingReview === 1 ? 'customer says they have' : 'customers say they have'}{' '}
              paid. Until each is confirmed the invoice stays open and they keep being chased.
            </span>

            <Button size="sm" onClick={onOpenPayments}>
              Review them
            </Button>
          </div>
        </Alert>
      )}

      <section>
        <h2 className="provider-h2">Money</h2>

        <div className="provider-grid">
          <MoneyTile label="Monthly recurring" totals={data.monthlyRecurring} />
          <MoneyTile label="Outstanding" totals={data.outstanding} tone="warning" />

          <Tile
            label="Invoices overdue"
            value={data.invoicesOverdue}
            tone={data.invoicesOverdue > 0 ? 'danger' : undefined}
          />
          <Tile
            label="Subscriptions past due"
            value={data.subscriptionsPastDue}
            tone={data.subscriptionsPastDue > 0 ? 'danger' : undefined}
          />
        </div>
      </section>

      <section>
        <h2 className="provider-h2">Customers</h2>

        <div className="provider-grid">
          <Tile label="Active" value={data.tenantsActive} tone="success" />
          <Tile label="On trial" value={data.tenantsTrialing} />
          <Tile
            label="Trials ending this week"
            value={data.trialsEndingSoon}
            tone={data.trialsEndingSoon > 0 ? 'warning' : undefined}
          />
          <Tile
            label="Suspended"
            value={data.tenantsSuspended}
            tone={data.tenantsSuspended > 0 ? 'danger' : undefined}
          />
        </div>
      </section>

      <section>
        <h2 className="provider-h2">Support</h2>

        <div className="provider-grid">
          <Tile label="Open" value={data.ticketsOpen} />
          <Tile
            label="Waiting on us"
            value={data.ticketsAwaitingUs}
            tone={data.ticketsAwaitingUs > 0 ? 'warning' : undefined}
          />
          <Tile
            label="Breaching today"
            value={data.ticketsBreachingToday}
            tone={data.ticketsBreachingToday > 0 ? 'danger' : undefined}
          />
        </div>
      </section>

      {data.tenantsByCountry.length > 0 && (
        <section>
          <h2 className="provider-h2">Where they are</h2>

          <div className="provider-chips">
            {data.tenantsByCountry.map((country) => (
              <Badge key={country.countryCode} tone="neutral">
                {country.countryName} · {country.tenants}
              </Badge>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * Money, stated once per currency.
 *
 * Never added together: a figure that sums rupees and Singapore dollars is not a number, and
 * the backend refuses to produce one for the same reason.
 */
function MoneyTile({
  label,
  totals,
  tone,
}: {
  label: string;
  totals: CurrencyTotal[];
  tone?: 'warning';
}) {
  return (
    <Card>
      <div className="provider-tile__label">{label}</div>

      {totals.length === 0 ? (
        <div className="provider-tile__value">—</div>
      ) : (
        totals.map((total) => (
          <div
            key={total.currency}
            className={`provider-tile__value${tone ? ` provider-tile__value--${tone}` : ''}`}
          >
            {formatMoney(total.amount, total.currency)}
          </div>
        ))
      )}
    </Card>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'success' | 'warning' | 'danger';
}) {
  return (
    <Card>
      <div className="provider-tile__label">{label}</div>
      <div className={`provider-tile__value${tone ? ` provider-tile__value--${tone}` : ''}`}>
        {value.toLocaleString()}
      </div>
    </Card>
  );
}
